// Helpers for the Excel report data (latest accepted inbound / outbound uploads)
import { EXCEPTION_LABELS, SHEET_LABELS } from "../ExcelUploadManager";

// Sum Control Tower aggregates across the inbound and outbound reports
export function combineExcel(reports) {
    const list = Object.values(reports || {});
    if (!list.length) return null;
    const sum = k => list.reduce((s, r) => s + (r.aggregates?.[k] || 0), 0);
    const pins = {};
    const RANK = { "#3B82F6":0, "#10B981":1, "#F59E0B":2, "#EF4444":3 };
    list.forEach(r => (r.aggregates?.mapLocations || []).forEach(p => {
        const cur = pins[p.name] || (pins[p.name] = { ...p, count: 0 });
        cur.count += p.count;
        if ((RANK[p.color] ?? 0) > (RANK[cur.color] ?? 0)) cur.color = p.color;
    }));
    const byCode = {};
    list.forEach(r => Object.entries(r.aggregates?.exceptionsByCode || {}).forEach(([k, v]) => { byCode[k] = (byCode[k] || 0) + v; }));
    const diff = k => list.reduce((s, r) => s + (r.diffSummary?.[k] || 0), 0);
    const lanes = list.flatMap(r => r.aggregates?.lanes || []);
    // Inbound and outbound totals use different keys, so merging just sums any overlap
    const totals = {};
    list.forEach(r => Object.entries(r.aggregates?.totals || {}).forEach(([k, v]) => { totals[k] = (totals[k] || 0) + v; }));
    return {
        lanes, totals,
        total: sum("total"), inTransit: sum("inTransit"), delivered: sum("delivered"), onTime: sum("onTime"),
        delayed: sum("delayed"), exceptions: sum("exceptions"), noEta: sum("noEta"),
        graceDays: list[0]?.aggregates?.graceDays ?? 1,
        // Target met on time, per report: inbound = received by ERD, outbound = delivered by required date
        met: Object.fromEntries(Object.entries(reports).map(([t, r]) => [t, { onTime: r.aggregates?.metOnTime || 0, late: r.aggregates?.metLate || 0 }])),
        mapLocations: Object.values(pins), exceptionsByCode: byCode,
        etaSlipped: diff("etaSlipped"), statusChanged: diff("statusChanged"), missing: diff("missing"),
        latestDate: list.map(r => r.reportDate).sort().pop(),
    };
}

export const hasAsn = text => /\basn/i.test(text || "");

// "Copy table" columns per report: [header, (row, statusLabels) => value]
const statusText = (r, L) => (r.status === "UNKNOWN" ? r.statusRaw : L[r.status] || r.status);
const scheduleText = r => ({ onTime: "On time", delayed: "Delayed", exception: "Exception", noEta: "No target date", delivered: "Delivered" })[r.schedule?.bucket] || "";
const exceptionsText = r => (r.exceptions || []).map(e => EXCEPTION_LABELS[e] || e).join("; ");
export const COPY_COLUMNS = {
    inbound: [
        ["PO", r => r.data.po_number], ["PO line", r => r.ref?.split(" / ")[1]], ["SKU", r => r.data.sku], ["Description", r => r.data.description],
        ["Container", r => r.data.equipment_number], ["Equipment size", r => r.data.equipment_size], ["Supplier", r => r.party],
        ["Origin", r => r.data.place_of_receipt_port], ["Destination", r => r.data.destination_city], ["Plant", r => r.data.plant_code],
        ["Carrier", r => r.data.carrier], ["Vessel", r => r.data.vessel], ["Qty", r => r.data.actual_quantity], ["Cartons", r => r.data.actual_packages],
        ["CBM", r => r.data.actual_measurement], ["ETA", r => r.eta], ["ERD", r => r.schedule?.target], ["Received", r => r.schedule?.actual],
        ["Status", statusText], ["Schedule", scheduleText], ["Reason", r => r.schedule?.reason], ["Exceptions", exceptionsText],
        ["Latest note", r => r.notes?.last], ["Notes", r => r.notes?.count || ""],
    ],
    outbound: [
        ["Connote / Shipment", r => r.ref], ["Type", r => SHEET_LABELS[r.sheetCode] || r.sheetCode], ["Receiver", r => r.party],
        ["Suburb", r => r.data.suburb], ["State", r => r.data.receiving_state], ["Delivery site", r => r.data.delivery_site],
        ["PO", r => r.data.purchase_order_number], ["Cartons", r => r.data.carton_count], ["Pallets (est.)", r => r.data.pallet_estimation],
        ["Required delivery", r => r.data.required_delivery_date], ["Status", statusText], ["Comments", r => r.data.comments],
        ["Schedule", scheduleText], ["Reason", r => r.schedule?.reason], ["Exceptions", exceptionsText],
        ["Latest note", r => r.notes?.last], ["Notes", r => r.notes?.count || ""],
    ],
};

// Rows → tab-separated text (header line first) that pastes straight into Excel
export function rowsToTsv(reportType, rows, statusLabels) {
    const cols = COPY_COLUMNS[reportType];
    const cell = v => (v == null ? "" : String(v).replace(/[\t\r\n]+/g, " "));
    return [cols.map(c => c[0]).join("\t"), ...rows.map(r => cols.map(c => cell(c[1](r, statusLabels))).join("\t"))].join("\n");
}

export async function copyText(text) {
    try {
        await navigator.clipboard.writeText(text);
    } catch {
        // Fallback when the Clipboard API is unavailable (e.g. non-secure context)
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("copy failed");
    }
}
